import { RFICorrespondence, RFIRequestInformation } from 'interfaces/RFIRequestForm';
import { SupplierEntry } from 'interfaces/RFXRequestForm';
import { dateTimeStringToTimestamp } from 'util/DatetimeUtils';
import { isBlank } from 'util/GeneralUtils';

export enum ErrorKey {
  // General information error keys
  RFINameMissing,
  BeginDateTimeMissing,
  DueDateTimeMissing,
  DateWithWrongOrder,
  RegionsMissing,
  CategoriesMissing,

  // Correspondence error keys
  SubjectMissing,
  BodyMissing,

  // Supplier error keys
  SupplierMissing,
  SupplierContactMissing
}

const ErrorMessages = {
  [ErrorKey.RFINameMissing]: 'RFI name is required.',
  [ErrorKey.BeginDateTimeMissing]: 'Begin date and time are required.',
  [ErrorKey.DueDateTimeMissing]: 'Required answer date and time are required.',
  [ErrorKey.DateWithWrongOrder]: 'Required answer date must be later than begin date.',
  [ErrorKey.RegionsMissing]: 'Regions are required.',
  [ErrorKey.CategoriesMissing]: 'Categories are required.',
  [ErrorKey.SubjectMissing]: 'Subject is required.',
  [ErrorKey.BodyMissing]: 'Message body is required.',
  [ErrorKey.SupplierMissing]: 'No suppliers has been added to the RFX event.',
  [ErrorKey.SupplierContactMissing]: 'Supplier contacts are required.'
};

export function getErrorMessage(key: ErrorKey): string {
  return ErrorMessages[key];
}

/**
 * Get error message for the correspondence section of the review step of the RFI request wizard,
 * based on the validation result
 *  
 * @param keys - validation errors
 */
export function getReviewStepMessageForCorrespondence(keys: ErrorKey[]): string {
  const bodyMissing = keys.includes(ErrorKey.BodyMissing);
  const subjectMissing = keys.includes(ErrorKey.SubjectMissing);

  if (!bodyMissing && !subjectMissing) return '';
  let text = '';
  if (bodyMissing && subjectMissing) {
    text = "Correspondence subject and body are missing."
  } else if(bodyMissing) {
    text = "Correspondence body is missing."
  } else if(subjectMissing) {
    text = "Correspondence subject is missing."
  }

  return text + " Please navigate to the Correspondence step and add the missing content."
}

export type RFIErrors = { [key in ErrorKey]: string };

/**
 * Check whether general information step of the RFI wizard has the valid data.
 *
 * Return error keys instead of error messages because different UI will show different error messages.
 */
export function validateGeneralInformation(generalInformation: RFIRequestInformation): ErrorKey[] {
  const { rfxName, beginDate, beginTime, dueDate, dueTime, regions, categories } = generalInformation;
  const errors: ErrorKey[] = [];

  if (isBlank(rfxName)) {
    errors.push(ErrorKey.RFINameMissing);
  }

  let beginTimestamp: number | undefined = undefined;
  let dueTimestamp: number | undefined = undefined;

  if (isBlank(beginDate) || isBlank(beginTime)) {
    errors.push(ErrorKey.BeginDateTimeMissing);
  } else {
    beginTimestamp = dateTimeStringToTimestamp({ dateString: beginDate, timeString: beginTime });
  }

  if (isBlank(dueDate) || isBlank(dueTime)) {
    errors.push(ErrorKey.DueDateTimeMissing);
  } else {
    dueTimestamp = dateTimeStringToTimestamp({ dateString: dueDate, timeString: dueTime });
  }

  if (beginTimestamp !== undefined && dueTimestamp !== undefined && beginTimestamp >= dueTimestamp) {
    errors.push(ErrorKey.DateWithWrongOrder);
  }

  if (regions.length === 0) {
    errors.push(ErrorKey.RegionsMissing);
  }

  if (categories.length === 0) {
    errors.push(ErrorKey.CategoriesMissing);
  }

  return errors;
}

/**
 * Check whether correspondence step of the RFI wizard has the valid data
 */
export function validateCorrespondence(correspondence: RFICorrespondence): ErrorKey[] {
  const errors: ErrorKey[] = [];

  if (isBlank(correspondence.subject)) {
    errors.push(ErrorKey.SubjectMissing);
  }

  if (isBlank(correspondence.body)) {
    errors.push(ErrorKey.BodyMissing);
  }

  return errors;
}

/**
 * Check whether suppliers step of the RFI wizard has the valid data
 */
export function validateSuppliers(supplierEntries: SupplierEntry[]): ErrorKey[] {
  const errors: ErrorKey[] = [];

  if (supplierEntries.length === 0) {
    errors.push(ErrorKey.SupplierMissing);
  } else if (supplierEntries.some((supplierEntry) => isBlank(supplierEntry.contactList))) {
    errors.push(ErrorKey.SupplierContactMissing);
  }

  return errors;
}
